export interface ContractorProfileData {
  companyName?: string;
  nip?: string;
  phone?: string;
  email?: string;
}

/** Placeholder / demo values that must never leave as a real client offer. */
const PLACEHOLDER_COMPANY_PREFIXES = ['twoja firma budowlana'];
const PLACEHOLDER_PHONE_DIGITS = ['123456789', '48123456789'];
/**
 * Sample NIPs that pass the checksum but are not the contractor's own number —
 * example values that used to be shown in the UI, docs and demo data. A valid
 * checksum alone cannot catch these, so they are blocked explicitly.
 */
const PLACEHOLDER_NIP_DIGITS = ['5260250274', '1111111111', '9999999999'];

function normalizeText(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function digitsOnly(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

/**
 * Polish NIP checksum validation (10 digits, weights 6·5·7·2·3·4·5·6·7).
 * A control result of 10 is never a valid NIP. The common placeholder
 * "1234567890" fails this check, so it is rejected automatically.
 */
export function isValidPolishNip(nip: string | undefined | null): boolean {
  const digits = digitsOnly(nip ?? '');
  if (digits.length !== 10) return false;
  if (/^(\d)\1{9}$/.test(digits)) return false;
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const sum = weights.reduce((acc, weight, index) => acc + weight * Number(digits[index]), 0);
  const control = sum % 11;
  if (control === 10) return false;
  return control === Number(digits[9]);
}

function isPlaceholderCompanyName(name: string): boolean {
  const normalized = normalizeText(name);
  // Any name starting with the demo phrase counts, so "Twoja Firma Budowlana Sp. z o.o."
  // (and spacing/case/dot variants) is rejected too.
  return PLACEHOLDER_COMPANY_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

function isPlaceholderPhone(phone: string): boolean {
  return PLACEHOLDER_PHONE_DIGITS.includes(digitsOnly(phone));
}

/** Sample/demo NIP that must never be printed as the contractor's own number. */
export function isPlaceholderNip(nip: string | undefined | null): boolean {
  return PLACEHOLDER_NIP_DIGITS.includes(digitsOnly(nip ?? ''));
}

/**
 * Returns the list of contractor fields that block a client PDF, each phrased so
 * the contractor knows exactly what to fix. Empty array = the profile is usable.
 * NIP stays optional, but when provided it must pass the checksum and must not be
 * a documented sample number; obvious demo company/phone values are rejected too.
 */
export function getMissingContractorProfileFields(
  profile: ContractorProfileData | null | undefined
): string[] {
  const missing: string[] = [];

  const companyName = profile?.companyName?.trim();
  if (!companyName) {
    missing.push('nazwa firmy');
  } else if (isPlaceholderCompanyName(companyName)) {
    missing.push('nazwa firmy (wpisz prawdziwą nazwę firmy)');
  }

  const phone = profile?.phone?.trim();
  const email = profile?.email?.trim();
  const hasEmail = Boolean(email);
  const phoneIsPlaceholder = Boolean(phone) && isPlaceholderPhone(phone as string);
  const hasUsablePhone = Boolean(phone) && !phoneIsPlaceholder;
  // A placeholder phone must be fixed even when a valid email is present — a fake
  // number cannot stay printed on the client offer.
  if (phoneIsPlaceholder) {
    missing.push('telefon (wpisz prawdziwy numer lub usuń wartość testową)');
  } else if (!hasUsablePhone && !hasEmail) {
    missing.push('telefon lub email');
  }

  const nip = profile?.nip?.trim();
  if (nip && isPlaceholderNip(nip)) {
    missing.push('NIP (wpisz własny numer lub usuń wartość przykładową)');
  } else if (nip && !isValidPolishNip(nip)) {
    missing.push('NIP (popraw numer — błędna suma kontrolna)');
  }

  return missing;
}
